import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { Col, Input, Row, Spin, notification } from "antd";

import { withProcedureWraper } from "../../../..";
import {
  ApprovalHistory,
  Box,
  BoxSection,
  ButtonList,
  Chats,
  Error,
  Icon,
  Loading,
  Steps,
} from "../../../../../../components";
import BoxSectionItem from "../../../../../../components/box-section/box-section-item";
import { useBCRequest, useBCSteps, useBCUpdate } from "../../hooks";
import { IBusinessCardResponse } from "../../models/IBusinessCard";
import { EnvHelper, StringHelper } from "../../../../../../helper";
import { EnvKey, RequestStatus } from "../../../../../../constants";
import { IAPItem } from "../../../../../../hook";
import { Routes } from "../../../../../../AppRoutes";

const fallback = {} as IBusinessCardResponse;
const siteUrl = EnvHelper.getValue(EnvKey.APPROVER_BC_URL);

const BCDetail = withProcedureWraper(() => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const [reason, setReason] = useState("");

  const {
    data: request = fallback,
    isFetching: isRequestFetching,
    isError,
  } = useBCRequest();
  const { mutate, isLoading: isUpdateLoading } = useBCUpdate();
  const { getStepItems, approve, getApprovalHistory, sendMailAP, updateSteps } =
    useBCSteps();
  const { stepNumber, stepStatus, isApprovedUser, userStep, canEdit } = approve(
    request.eApprovalProcess
  );

  const handleApprove = () => {
    const newStep: IAPItem = {
      ...userStep,
      status: RequestStatus.approved,
      reason,
    };
    const newApprovalProcess = updateSteps(request.eApprovalProcess, newStep);
    const updatedFields = {
      eApprovalProcess: newApprovalProcess,
      eRequestStatus: userStep.requestStatus,
    };
    mutate(
      { id: request.id!, body: updatedFields },
      {
        onSuccess(response: any) {
          queryClient.invalidateQueries({
            queryKey: ["BC-requests"],
          });
          sendMailAP(response.eApprovalProcess, {
            toPath: `${siteUrl}/${response.id}`,
            type: RequestStatus.approved,
          });
          notification.success({
            message: "Thành công!",
            description: `Duyệt yêu cầu thành công.`,
          });
        },
        onError() {
          notification.error({
            message: "Thất bại",
            description: `Duyệt yêu cầu thất bại.`,
          });
        },
      }
    );
  };
  const handleReject = () => {
    const newApprovalProcess = updateSteps(request?.eApprovalProcess, {
      ...userStep,
      status: RequestStatus.rejected,
      reason,
    });
    const updatedFields = {
      eApprovalProcess: newApprovalProcess,
      eRequestStatus: RequestStatus.rejected,
    };
    mutate(
      { id: request.id!, body: updatedFields },
      {
        onSuccess(response: any) {
          queryClient.invalidateQueries({
            queryKey: ["BC-requests"],
          });
          sendMailAP(response.eApprovalProcess, {
            toPath: `${siteUrl}/${response.id}`,
            type: RequestStatus.rejected,
          });
          notification.success({
            message: "Thành công!",
            description: `Từ chối yêu cầu thành công.`,
          });
        },
        onError() {
          notification.error({
            message: "Thất bại",
            description: `Từ chối yêu cầu thất bại.`,
          });
        },
      }
    );
  };
  if (isRequestFetching) return <Loading />;
  if (isError) return <Error description="Không tìm thấy dữ liệu" />;
  return (
    <Spin spinning={isUpdateLoading}>
      <div className="pd15">
        <div className="PageHead flex-center">
          <div className="title" style={{ margin: 0 }}>
            <div className="btnBack" onClick={() => history.goBack()}>
              <Icon.ArrowLeft />
            </div>
            Xem xét yêu cầu phê duyệt
          </div>
        </div>
        <Box title="Quy trình duyệt">
          <Steps
            items={getStepItems(request?.eApprovalProcess)}
            status={stepStatus}
            current={stepNumber}
          />
        </Box>
        <Box title="Thông tin">
          <BoxSection title="Thông tin phiếu in danh thiếp">
            <Row gutter={[32, 0]}>
              <Col span={12}>
                <BoxSectionItem
                  textLeft="Họ tên người yêu cầu"
                  textRight={request?.eName}
                />
              </Col>
              <Col span={12}>
                <BoxSectionItem
                  textLeft="Cơ quan/Đơn vị"
                  textRight={
                    StringHelper.verticalBarSplit<string[]>(
                      request?.eDepartment
                    )[1]
                  }
                />
              </Col>
              <Col span={12}>
                <BoxSectionItem
                  textLeft="Đơn vị"
                  textRight={
                    StringHelper.verticalBarSplit<string[]>(request?.eUnit)[1]
                  }
                />
              </Col>
            </Row>
          </BoxSection>
          <BoxSection title="Thông tin chi tiết">
            <Row gutter={[32, 0]}>
              <Col span={12}>
                <BoxSectionItem
                  textLeft="Họ và tên"
                  textRight={request?.eCardName}
                />
              </Col>
              <Col span={12}>
                <BoxSectionItem
                  textLeft="Chức danh"
                  textRight={request?.eCardTitle}
                />
              </Col>
              <Col span={12}>
                <BoxSectionItem
                  textLeft="Email"
                  textRight={request?.eCardEmail}
                />
              </Col>
              <Col span={12}>
                <BoxSectionItem
                  textLeft="Địa chỉ"
                  textRight={request?.eCardAddress}
                />
              </Col>
              <Col span={12}>
                <BoxSectionItem
                  textLeft="Địa chỉ website"
                  textRight={request?.eCardSite}
                />
              </Col>
              <Col span={12}>
                <BoxSectionItem
                  textLeft="Số điện thoại"
                  textRight={request?.eCardPhone}
                />
              </Col>
              <Col span={12}>
                <BoxSectionItem
                  textLeft="Ghi chú"
                  textRight={request?.eDescription}
                />
              </Col>
            </Row>
          </BoxSection>
          <BoxSection title="Lịch sử phê duyệt">
            <ApprovalHistory
              items={getApprovalHistory(request?.eApprovalProcess)}
            />
            {isApprovedUser && (
              <div className="pd15">
                <Input.TextArea
                  placeholder="Nhập lý do..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            )}
          </BoxSection>
          <BoxSection title="Thông tin trao đổi">
            <Chats
              items={request.eComments}
              mutateFn={mutate as any}
              requestId={request.id}
            />
          </BoxSection>
        </Box>
        <div className="flex-center">
          <ButtonList
            items={[
              {
                buttonType: "success",
                label: "Đồng ý",
                hidden: !isApprovedUser,
                onClick: handleApprove,
              },
              {
                buttonType: "grey",
                label: "Không đồng ý",
                hidden: !isApprovedUser,
                onClick: handleReject,
              },
              {
                buttonType: "warning",
                label: "Chỉnh sửa",
                hidden: !canEdit,
                onClick: () =>
                  history.push(
                    `${Routes.businessCard.path}/${request.id}/edit`
                  ),
              },
            ]}
          />
        </div>
      </div>
    </Spin>
  );
});

export default BCDetail;
